import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Divider,
  Tabs,
  Tab,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Alert,
  Snackbar,
  InputAdornment,
  Chip,
  useTheme,
  useMediaQuery,
  alpha,
  LinearProgress,
  Avatar,
  Badge,
  TableSortLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
  Download as DownloadIcon,
  Info as InfoIcon,
  DataUsage as DataUsageIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Dashboard as DashboardIcon,
  Clear as ClearIcon,
  Sort as SortIcon
} from '@mui/icons-material';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { adminService } from '../services';


// Mock data for tables and columns
const mockTables = {
  sales: [
    { id: 1, name: 'Opportunity', description: 'Sales opportunities and deals', columns: ['ID', 'Name', 'Customer', 'Value', 'Stage', 'Probability', 'Close Date'] },
    { id: 2, name: 'Proposal_Part_Detail', description: 'Parts included in sales proposals', columns: ['ID', 'Proposal_ID', 'Part_Number', 'Quantity', 'Price', 'Extended_Price'] },
    { id: 3, name: 'Outage', description: 'Customer outages and service events', columns: ['ID', 'Customer', 'Start_Date', 'End_Date', 'Status', 'Type'] },
    { id: 4, name: 'Account', description: 'Customer accounts', columns: ['ID', 'Name', 'Address', 'City', 'State', 'Country', 'Industry'] }
  ],
  cost: [
    { id: 5, name: 'Part_MSA_Cost', description: 'Cost information for parts', columns: ['ID', 'Part_Number', 'Cost', 'Vendor', 'Effective_Date', 'Expiration_Date'] },
    { id: 6, name: 'PO_Detail', description: 'Purchase order details', columns: ['ID', 'PO_Number', 'Vendor_ID', 'Part_Number', 'Quantity', 'Unit_Cost', 'Extended_Cost'] },
    { id: 7, name: 'Supplier', description: 'Supplier information', columns: ['ID', 'Name', 'Address', 'Contact_Person', 'Phone', 'Email', 'Terms'] },
    { id: 8, name: 'Global_Part_Whse', description: 'Warehouse and inventory information', columns: ['ID', 'Part_Number', 'Warehouse', 'Quantity', 'Location', 'Bin', 'Cost'] }
  ]
};

// --- Main Admin Portal Component ---
export default function AdminPortal() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();  const { instance, accounts } = useMsal();
  const isAuthenticated = accounts.length > 0;
  const userName = accounts.length > 0 ? accounts[0].name || accounts[0].username : "";
  const userUpn = accounts.length > 0 ? accounts[0].username || accounts[0].homeAccountId?.split('.')[0] : "";
  
  // State for admin access control
  const [hasAdminAccess, setHasAdminAccess] = useState(null); // null = checking, true = has access, false = no-data-access
  const [adminCheckLoading, setAdminCheckLoading] = useState(true);
  
  // State for active tab
  const [activeTab, setActiveTab] = useState(0);
  
  // State for user management
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [roleFilter, setRoleFilter] = useState('');
  
  // State for sorting and filtering
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filterConfig, setFilterConfig] = useState({
    role: '',
    isAdmin: '',
    hasPortalAccess: ''
  });
  const [appliedFilters, setAppliedFilters] = useState([]);
  
  // State for table permissions
  const [tablePermissions, setTablePermissions] = useState({ sales: true, cost: false });
  const [selectedTable, setSelectedTable] = useState(null);
  
  // State for toast messages
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  // Fetch users from API
  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const token = await instance.acquireTokenSilent({
        scopes: ["user.read"],
        account: accounts[0]
      });
      
      const data = await adminService.getUsers(searchQuery, roleFilter, token.accessToken);
      setUsers(data);
    } catch (error) {
      console.error('Error fetching users:', error);
      setSnackbar({
        open: true,
        message: 'Error fetching users: ' + (error.message || 'Please try again.'),
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };  // Check admin access based on user UPN
  const checkAdminAccess = async () => {
    if (!isAuthenticated || !userUpn) {
      setHasAdminAccess(false);
      setAdminCheckLoading(false);
      return;
    }

    try {
      setAdminCheckLoading(true);
      
      const token = await instance.acquireTokenSilent({
        scopes: ["user.read"],
        account: accounts[0]
      });
      
      const data = await adminService.checkAdminAccess(token.accessToken);
      console.log(data)
      setHasAdminAccess(data.hasAccess);
      
      if (!data.hasAccess) {
        setSnackbar({
          open: true,
          message: data.message || 'You do not have admin access to this portal.',
          severity: 'warning'
        });
      }
    } catch (error) {
      console.error('Error checking admin access:', error);
      setHasAdminAccess(false);
      setSnackbar({
        open: true,
        message: 'Error verifying admin access: ' + (error.message || 'Please contact support.'),
        severity: 'error'
      });
    } finally {
      setAdminCheckLoading(false);
    }
  };
  
  console.log('AdminPortal rendered with user:', userName, 'UPN:', userUpn, "isAuthenticated", isAuthenticated, 'hasAdminAccess:', hasAdminAccess);
  // Check for admin privileges and load initial data
  useEffect(() => {
    if (isAuthenticated) {
      checkAdminAccess();
    } else {
      // Redirect unauthenticated users
      console.log("User is not authenticated, redirecting to login");
      navigate('/');
    }
  }, [isAuthenticated]);

  // Load users only if admin access is confirmed
  useEffect(() => {
    if (hasAdminAccess === true) {
      fetchUsers();
    }
  }, [hasAdminAccess, searchQuery, roleFilter]);
  
  // Handle tab changes
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);  };
  
  // Open user dialog for editing or creating
  const handleOpenUserDialog = (user = null) => {
    if (user) {
      setSelectedUser({...user});
    } else {
      setSelectedUser({
        name: '',
        upn: '',
        role: 'no-data-access',
        isAdmin: false,
        downloadLimit: 0,
        hasPortalAccess: true
      });
    }
    setUserDialogOpen(true);
  };
  
  // Close user dialog
  const handleCloseUserDialog = () => {
    setUserDialogOpen(false);
    setSelectedUser(null);
  };  // Save user changes
  const handleSaveUser = async () => {
    if (!selectedUser) return;
    
    try {
      const token = await instance.acquireTokenSilent({
        scopes: ["user.read"],
        account: accounts[0]
      });
      
      let result;
      
      if (selectedUser.id) {
        // Update existing user
        result = await adminService.updateUser(selectedUser.id, selectedUser, token.accessToken);
      } else {
        // Create new user
        result = await adminService.createUser(selectedUser, token.accessToken);
      }
      
      setSnackbar({
        open: true,
        message: `User ${selectedUser.id ? 'updated' : 'created'} successfully`,
        severity: 'success'
      });
      handleCloseUserDialog();
      fetchUsers(); // Refresh user list
    } catch (error) {
      console.error('Error saving user:', error);
      setSnackbar({
        open: true,
        message: `Error saving user: ${error.message}`,
        severity: 'error'
      });
    }
  };
    // Delete a user
  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }
    
    try {
      const token = await instance.acquireTokenSilent({
        scopes: ["user.read"],
        account: accounts[0]
      });
      
      await adminService.deleteUser(userId, token.accessToken);
      
      setSnackbar({
        open: true,
        message: 'User deleted successfully',
        severity: 'success'
      });
      fetchUsers(); // Refresh user list
    } catch (error) {
      console.error('Error deleting user:', error);
      setSnackbar({
        open: true,
        message: `Error deleting user: ${error.message}`,
        severity: 'error'
      });
    }
  };
  
  // Handle user field changes
  const handleUserFieldChange = (field, value) => {
    setSelectedUser({
      ...selectedUser,
      [field]: value
    });
  };

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    const newFilterConfig = { ...filterConfig, [filterType]: value };
    setFilterConfig(newFilterConfig);
    
    // Update applied filters for display
    const newAppliedFilters = Object.entries(newFilterConfig)
      .filter(([key, val]) => val !== '')
      .map(([key, val]) => ({ key, value: val, label: getFilterLabel(key, val) }));
    
    setAppliedFilters(newAppliedFilters);
  };

  // Clear a specific filter
  const clearFilter = (filterKey) => {
    handleFilterChange(filterKey, '');
  };

  // Clear all filters
  const clearAllFilters = () => {
    setFilterConfig({ role: '', isAdmin: '', hasPortalAccess: '' });
    setAppliedFilters([]);
  };

  // Get filter label for display
  const getFilterLabel = (key, value) => {
    switch (key) {
      case 'role':
        return `Role: ${value}`;
      case 'isAdmin':
        return `Admin: ${value === 'true' ? 'Yes' : 'No'}`;
      case 'hasPortalAccess':
        return `Portal Access: ${value === 'true' ? 'Yes' : 'No'}`;
      default:
        return `${key}: ${value}`;
    }
  };

  // Sort users based on current sort configuration
  const getSortedUsers = (usersToSort) => {
    if (!sortConfig.key) return usersToSort;
    
    return [...usersToSort].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      
      // Handle different data types
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const result = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
        return sortConfig.direction === 'asc' ? result : -result;
      }
      
      if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
        const result = aValue === bValue ? 0 : aValue ? 1 : -1;
        return sortConfig.direction === 'asc' ? result : -result;
      }
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        const result = aValue - bValue;
        return sortConfig.direction === 'asc' ? result : -result;
      }
      
      // Default string comparison
      const result = String(aValue).localeCompare(String(bValue));
      return sortConfig.direction === 'asc' ? result : -result;
    });
  };

  // Filtered and sorted users based on search and filters
  const filteredUsers = getSortedUsers(users.filter(user => {
    const userName = user.name || '';
    const userUpn = user.upn || '';
    const userRole = user.role || '';
    
    // Text search filter
    const matchesSearch = userName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         userUpn.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         userRole.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Role filter
    const matchesRole = !filterConfig.role || user.role === filterConfig.role;
    
    // Admin filter
    const matchesAdmin = !filterConfig.isAdmin || 
                        String(user.isAdmin) === filterConfig.isAdmin;
    
    // Portal access filter
    const matchesPortalAccess = !filterConfig.hasPortalAccess || 
                               String(user.hasPortalAccess) === filterConfig.hasPortalAccess;
    
    return matchesSearch && matchesRole && matchesAdmin && matchesPortalAccess;
  }));
    // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    try {
      return new Date(dateString).toLocaleString();
    } catch (error) {
      return 'Invalid Date';
    }
  };
  
  // Handle permission changes for tables
  const toggleTablePermission = (tableType, allowed) => {
    setTablePermissions({
      ...tablePermissions,
      [tableType]: allowed
    });
  };

  // Go back to main conversation page
  const handleBackToMain = () => {
    // Clear the admin route flag since user is leaving admin page
    sessionStorage.removeItem('current_route');
    navigate('/');
  };

  return (
    <Box sx={{ flexGrow: 1, height: '100vh', overflow: 'auto', bgcolor: 'background.default' }}>
      {/* Show loading while checking admin access */}
      {adminCheckLoading && (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '100vh',
          gap: 2
        }}>
          <LinearProgress sx={{ width: '200px' }} />
          <Typography variant="h6" color="text.secondary">
            Verifying admin access...
          </Typography>
        </Box>
      )}

      {/* Show no-data-access message if user doesn't have admin privileges */}
      {!adminCheckLoading && hasAdminAccess === false && (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '100vh',
          gap: 3,
          px: 3
        }}>
          <Box sx={{ textAlign: 'center' }}>
            <SecurityIcon sx={{ fontSize: 100, color: 'error.main', mb: 2 }} />
            <Typography variant="h4" color="error.main" gutterBottom>
              Access Denied
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
              You do not have admin privileges to access this portal.
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 500 }}>
              If you believe this is an error, please contact your system administrator to request admin access.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button 
                variant="contained" 
                startIcon={<ArrowBackIcon />}
                onClick={handleBackToMain}
                sx={{ 
                  bgcolor: '#1976D2',
                  '&:hover': { bgcolor: '#1565C0' }
                }}
              >
                Back to Main
              </Button>
              <Button 
                variant="outlined" 
                startIcon={<RefreshIcon />}
                onClick={checkAdminAccess}
              >
                Retry
              </Button>
            </Box>          </Box>
        </Box>
      )}

      {/* Show admin portal if user has access */}
      {!adminCheckLoading && hasAdminAccess === true && (
        <>
          {/* Header */}
          <Box 
            sx={{ 
              py: { xs: 1.5, sm: 2 }, 
              px: { xs: 2, sm: 3 }, 
              borderBottom: 1, 
              borderColor: 'divider',
              bgcolor: 'background.paper',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              boxShadow: '0 2px 5px rgba(0,0,0,0.05)',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 2, sm: 0 }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: { xs: '100%', sm: 'auto' } }}>
              <IconButton 
                onClick={handleBackToMain}
                sx={{ 
                  mr: 2, 
                  color: '#1976D2',
                  '&:hover': {
                    bgcolor: alpha('#1976D2', 0.08),
                  }
                }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                <DashboardIcon sx={{ color: '#1976D2', mr: 1, display: { xs: 'none', sm: 'block' } }} />
                <Typography 
                  variant={{ xs: 'h6', sm: 'h5' }} 
                  sx={{ 
                    fontWeight: 600, 
                    color: 'text.primary',
                    fontSize: { xs: '1.1rem', sm: '1.5rem' }
                  }}
                >
                  Parts360 Admin Portal
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'center', sm: 'flex-end' } }}>                <Chip 
                avatar={
                  <Avatar sx={{ bgcolor: '#1976D2', width: { xs: 24, sm: 32 }, height: { xs: 24, sm: 32 } }}>
                    {userName ? userName.charAt(0).toUpperCase() : "A"}
                  </Avatar>
                }
                label={String(userName || "Admin User")} 
                variant="outlined"
                size={isMobile ? 'small' : 'medium'}
                sx={{ 
                  borderColor: '#1976D2',
                  '& .MuiChip-label': {
                    fontWeight: 500,
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }
                }}
              />
            </Box>
          </Box>
        <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 4 }, mb: { xs: 2, sm: 4 }, px: { xs: 1, sm: 3 } }}>
        {/* Status Dashboard */}
        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 3, sm: 4 } }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card 
              raised 
              sx={{ 
                height: '100%', 
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                transition: 'transform 0.3s',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                }
              }}
            >              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976D2', fontSize: { xs: '1rem', sm: '1.25rem' } }}>Total Users</Typography>
                  <Avatar sx={{ bgcolor: '#E3F2FD', width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 } }}>
                    <Icon icon="ph:users-three" width={24} height={24} style={{ color: '#1976D2' }} />
                  </Avatar>
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 700, color: 'text.primary', fontSize: { xs: '1.5rem', sm: '3rem' } }}>
                  {users.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card 
              raised 
              sx={{ 
                height: '100%', 
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                transition: 'transform 0.3s',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                }
              }}
            >              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#424242', fontSize: { xs: '1rem', sm: '1.25rem' } }}>Admin Users</Typography>
                  <Avatar sx={{ bgcolor: '#F5F5F5', width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 } }}>
                    <Icon icon="carbon:user-admin" width={24} height={24} style={{ color: '#424242' }} />
                  </Avatar>
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 700, color: 'text.primary', fontSize: { xs: '1.5rem', sm: '3rem' } }}>
                  {users.filter(u => u.isAdmin).length}
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={(users.filter(u => u.isAdmin).length / users.length) * 100} 
                    sx={{ 
                      height: 6, 
                      borderRadius: 3,
                      bgcolor: '#F5F5F5',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: '#424242'
                      }
                    }} 
                  />
                  <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: 'text.secondary', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                    {Math.round((users.filter(u => u.isAdmin).length / users.length) * 100)}% of users have admin access
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          {/* <Grid item xs={12} md={3}>
            <Card 
              raised 
              sx={{ 
                height: '100%', 
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                transition: 'transform 0.3s',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976D2' }}>Data Tables</Typography>
                  <Avatar sx={{ bgcolor: '#E3F2FD' }}>
                    <Icon icon="carbon:data-table" width={24} height={24} style={{ color: '#1976D2' }} />
                  </Avatar>
                </Box>                <Typography variant="h3" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  {mockTables.sales.length + mockTables.cost.length}
                </Typography>
                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                  <Badge badgeContent={mockTables.sales.length} color="primary" sx={{ '& .MuiBadge-badge': { bgcolor: '#1976D2' } }}>
                    <Chip 
                      label="Sales" 
                      size="small" 
                      sx={{ 
                        bgcolor: '#E3F2FD', 
                        color: '#1976D2', 
                        fontWeight: 500,
                        px: 0.5 
                      }} 
                    />
                  </Badge>
                  <Badge badgeContent={mockTables.cost.length} color="default" sx={{ '& .MuiBadge-badge': { bgcolor: '#757575' } }}>
                    <Chip 
                      label="Cost" 
                      size="small" 
                      sx={{ 
                        bgcolor: '#F5F5F5', 
                        color: '#757575', 
                        fontWeight: 500,
                        px: 0.5 
                      }} 
                    />
                  </Badge>
                </Box>
              </CardContent>
            </Card>
          </Grid>
           */}          <Grid item xs={12} sm={6} md={3}>
            <Card 
              raised 
              sx={{ 
                height: '100%', 
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                transition: 'transform 0.3s',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                }
              }}
            >              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#424242', fontSize: { xs: '1rem', sm: '1.25rem' } }}>Access Roles</Typography>
                  <Avatar sx={{ bgcolor: '#F5F5F5', width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 } }}>
                    <Icon icon="carbon:user-role" width={24} height={24} style={{ color: '#424242' }} />
                  </Avatar>
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 700, color: 'text.primary', fontSize: { xs: '1.5rem', sm: '3rem' } }}>
                  4
                </Typography>
                <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  <Chip size="small" label="all-data-access" sx={{ bgcolor: '#E3F2FD', color: '#1976D2', fontWeight: 500, fontSize: { xs: '0.65rem', sm: '0.75rem' } }} />
                  <Chip size="small" label="except-cost-data" sx={{ bgcolor: '#E3F2FD', color: '#1976D2', fontWeight: 500, fontSize: { xs: '0.65rem', sm: '0.75rem' } }} />
                  <Chip size="small" label="except-sales-data" sx={{ bgcolor: '#F5F5F5', color: '#424242', fontWeight: 500, fontSize: { xs: '0.65rem', sm: '0.75rem' } }} />
                  <Chip size="small" label="no-data-access" sx={{ bgcolor: '#F5F5F5', color: '#757575', fontWeight: 500, fontSize: { xs: '0.65rem', sm: '0.75rem' } }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>        {/* Tabs for different admin sections */}
        <Paper 
          elevation={0} 
          sx={{ 
            borderRadius: '12px', 
            mb: { xs: 2, sm: 3 }, 
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
          }}
        >
          {/* Tab Navigation */}
          {/* <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant={isMobile ? "scrollable" : "fullWidth"}
            scrollButtons={isMobile ? "auto" : false}
            allowScrollButtonsMobile={isMobile}
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: alpha('#1976D2', 0.02),
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: { xs: '0.75rem', sm: '1rem' },
                minHeight: { xs: 48, sm: 56 },
                minWidth: { xs: 120, sm: 'auto' },
                padding: { xs: '8px 12px', sm: '12px 16px' },
                '&.Mui-selected': {
                  color: '#1976D2',
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#1976D2',
                height: 3,
              },
              '& .MuiTabs-scrollButtons': {
                color: '#1976D2',
              },
            }}
          >
            <Tab 
              icon={<Icon icon="mdi:account-cog" width={isMobile ? 16 : 20} height={isMobile ? 16 : 20} />}
              label="User Management" 
              iconPosition="start"
              sx={{ gap: { xs: 0.5, sm: 1 } }}
            />
            <Tab 
              icon={<Icon icon="mdi:table-cog" width={isMobile ? 16 : 20} height={isMobile ? 16 : 20} />}
              label="Data Access Control" 
              iconPosition="start"
              sx={{ gap: { xs: 0.5, sm: 1 } }}
            />
            <Tab 
              icon={<Icon icon="mdi:download-box" width={isMobile ? 16 : 20} height={isMobile ? 16 : 20} />}
              label="Download Limits" 
              iconPosition="start"
              sx={{ gap: { xs: 0.5, sm: 1 } }}
            />
          </Tabs> */}
            {/* User Management Tab */}
          {activeTab === 0 && (
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: { xs: 'flex-start', sm: 'center' }, 
                justifyContent: 'space-between', 
                mb: 3,
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 2, sm: 0 }
              }}>
                <Typography variant="h6" sx={{ 
                  fontWeight: 600, 
                  color: '#1976D2', 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  fontSize: { xs: '1.1rem', sm: '1.25rem' }
                }}>
                  <Icon icon="mdi:account-cog" width={24} height={24} />
                  Manage Users and Roles
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  gap: 2, 
                  width: { xs: '100%', sm: 'auto' },
                  flexDirection: { xs: 'column', sm: 'row' }
                }}>
                  <TextField
                    placeholder="Search users..."
                    variant="outlined"
                    size="small"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" sx={{ color: alpha('#1976D2', 0.7) }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ 
                      width: { xs: '100%', sm: 250 },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: alpha('#1976D2', 0.5),
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#1976D2',
                        }
                      }
                    }}
                  />
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenUserDialog()}
                    fullWidth={{ xs: true, sm: false }}
                    sx={{ 
                      fontWeight: 500,
                      textTransform: 'none',
                      borderRadius: 2,
                      bgcolor: '#1976D2',
                      '&:hover': {
                        bgcolor: '#1565C0',
                      }
                    }}
                  >
                    Add User
                  </Button>
                </Box>
              </Box>

              {/* Results Summary with Integrated Filters */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: { xs: 'flex-start', lg: 'center' },
                justifyContent: 'space-between',
                mb: 3,
                py: { xs: 2, sm: 2 },
                px: { xs: 2, sm: 2.5 },
                bgcolor: alpha('#1976D2', 0.04),
                borderRadius: 2,
                flexDirection: { xs: 'column', lg: 'row' },
                gap: { xs: 2, lg: 1 }
              }}>
                {/* Results info and active filters */}
                <Box sx={{ 
                  width: { xs: '100%', lg: 'auto' }, 
                  flex: { lg: '0 1 auto' },
                  maxWidth: { lg: '50%' }
                }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 1 }}>
                    Showing {filteredUsers.length} of {users.length} users
                    {appliedFilters.length > 0 && ' (filtered)'}
                    {sortConfig.key && (
                      <Chip
                        size="small"
                        icon={sortConfig.direction === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
                        label={`Sorted by ${sortConfig.key}`}
                        sx={{
                          ml: 1,
                          height: 20,
                          fontSize: '0.7rem',
                          bgcolor: alpha('#1976D2', 0.1),
                          color: '#1976D2'
                        }}
                      />
                    )}
                  </Typography>
                  
                  {/* Applied Filters Display */}
                  {appliedFilters.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        Active Filters:
                      </Typography>
                      {appliedFilters.map((filter, index) => (
                        <Chip
                          key={index}
                          label={filter.label}
                          size="small"
                          onDelete={() => clearFilter(filter.key)}
                          deleteIcon={<ClearIcon fontSize="small" />}
                          sx={{
                            bgcolor: alpha('#1976D2', 0.1),
                            color: '#1976D2',
                            height: 20,
                            fontSize: '0.7rem',
                            '& .MuiChip-deleteIcon': {
                              color: alpha('#1976D2', 0.7),
                              '&:hover': {
                                color: '#1976D2',
                              }
                            }
                          }}
                        />
                      ))}
                    </Box>
                  )}
                  
                  {isLoading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <LinearProgress sx={{ width: 100, height: 2 }} />
                      <Typography variant="caption" color="text.secondary">Loading...</Typography>
                    </Box>
                  )}
                </Box>

                {/* Filter Controls */}
                <Box sx={{ 
                  width: { xs: '100%', lg: 'auto' },
                  flex: { lg: '0 0 auto' }
                }}>
                  <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 1, sm: 1 },
                    alignItems: { xs: 'stretch', sm: 'center' },
                    justifyContent: { lg: 'flex-end' }
                  }}>
                    {/* Filters Label - only show on desktop */}
                    <Typography variant="body2" sx={{ 
                      fontWeight: 500, 
                      color: 'text.secondary', 
                      display: { xs: 'flex', sm: 'flex' }, 
                      alignItems: 'center', 
                      gap: 0.5,
                      fontSize: '0.875rem',
                      minWidth: 'auto',
                      whiteSpace: 'nowrap'
                    }}>
                      <FilterIcon fontSize="small" />
                      Filters:
                    </Typography>
                    
                    {/* Role Filter */}
                    <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 110 } }}>
                      <InputLabel>Role</InputLabel>
                      <Select
                        value={filterConfig.role}
                        onChange={(e) => handleFilterChange('role', e.target.value)}
                        label="Role"
                      >
                        <MenuItem value="">All Roles</MenuItem>
                        <MenuItem value="all-data-access">All Access</MenuItem>
                        <MenuItem value="except-cost-data">No Cost</MenuItem>
                        <MenuItem value="except-sales-data">No Sales</MenuItem>
                        <MenuItem value="no-data-access">No Access</MenuItem>
                      </Select>
                    </FormControl>

                    {/* Admin Filter */}
                    <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 110 } }}>
                      <InputLabel>Admin</InputLabel>
                      <Select
                        value={filterConfig.isAdmin}
                        onChange={(e) => handleFilterChange('isAdmin', e.target.value)}
                        label="Admin"
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="true">Yes</MenuItem>
                        <MenuItem value="false">No</MenuItem>
                      </Select>
                    </FormControl>

                    {/* Portal Access Filter */}
                    <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 90 } }}>
                      <InputLabel>Portal</InputLabel>
                      <Select
                        value={filterConfig.hasPortalAccess}
                        onChange={(e) => handleFilterChange('hasPortalAccess', e.target.value)}
                        label="Portal"
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="true">Enabled</MenuItem>
                        <MenuItem value="false">Disabled</MenuItem>
                      </Select>
                    </FormControl>

                    {/* Clear All Filters Button */}
                    {appliedFilters.length > 0 && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ClearIcon />}
                        onClick={clearAllFilters}
                        sx={{
                          borderColor: alpha('#1976D2', 0.5),
                          color: '#1976D2',
                          minWidth: { xs: '100%', sm: 'auto' },
                          px: 2,
                          whiteSpace: 'nowrap',
                          '&:hover': {
                            borderColor: '#1976D2',
                            bgcolor: alpha('#1976D2', 0.04),
                          }
                        }}
                      >
                        Clear All
                      </Button>
                    )}
                  </Box>
                </Box>
              </Box>
              
              <TableContainer 
                component={Paper} 
                variant="outlined" 
                sx={{ 
                  borderRadius: '12px', 
                  borderColor: 'divider',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                  overflow: 'hidden',
                  overflowX: 'auto',
                  maxWidth: '100%'
                }}
              >
                <Table sx={{ 
                  minWidth: { xs: 600, sm: 800 },
                  width: '100%'
                }}>
                  <TableHead sx={{ bgcolor: alpha('#1976D2', 0.08) }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, py: 2, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                        <TableSortLabel
                          active={sortConfig.key === 'name'}
                          direction={sortConfig.key === 'name' ? sortConfig.direction : 'asc'}
                          onClick={() => handleSort('name')}
                          sx={{
                            '& .MuiTableSortLabel-icon': {
                              color: '#1976D2 !important',
                            },
                            '&:hover': {
                              color: '#1976D2',
                            },
                            '&.Mui-active': {
                              color: '#1976D2',
                            }
                          }}
                        >
                          Name
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, py: 2, fontSize: { xs: '0.8rem', sm: '0.875rem' }, display: { xs: 'none', sm: 'table-cell' } }}>
                        <TableSortLabel
                          active={sortConfig.key === 'upn'}
                          direction={sortConfig.key === 'upn' ? sortConfig.direction : 'asc'}
                          onClick={() => handleSort('upn')}
                          sx={{
                            '& .MuiTableSortLabel-icon': {
                              color: '#1976D2 !important',
                            },
                            '&:hover': {
                              color: '#1976D2',
                            },
                            '&.Mui-active': {
                              color: '#1976D2',
                            }
                          }}
                        >
                          UPN
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, py: 2, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                        <TableSortLabel
                          active={sortConfig.key === 'role'}
                          direction={sortConfig.key === 'role' ? sortConfig.direction : 'asc'}
                          onClick={() => handleSort('role')}
                          sx={{
                            '& .MuiTableSortLabel-icon': {
                              color: '#1976D2 !important',
                            },
                            '&:hover': {
                              color: '#1976D2',
                            },
                            '&.Mui-active': {
                              color: '#1976D2',
                            }
                          }}
                        >
                          Role
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, py: 2, fontSize: { xs: '0.8rem', sm: '0.875rem' }, display: { xs: 'none', md: 'table-cell' } }}>
                        <TableSortLabel
                          active={sortConfig.key === 'isAdmin'}
                          direction={sortConfig.key === 'isAdmin' ? sortConfig.direction : 'asc'}
                          onClick={() => handleSort('isAdmin')}
                          sx={{
                            '& .MuiTableSortLabel-icon': {
                              color: '#1976D2 !important',
                            },
                            '&:hover': {
                              color: '#1976D2',
                            },
                            '&.Mui-active': {
                              color: '#1976D2',
                            }
                          }}
                        >
                          Admin
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, py: 2, fontSize: { xs: '0.8rem', sm: '0.875rem' }, display: { xs: 'none', md: 'table-cell' } }}>
                        <TableSortLabel
                          active={sortConfig.key === 'hasPortalAccess'}
                          direction={sortConfig.key === 'hasPortalAccess' ? sortConfig.direction : 'asc'}
                          onClick={() => handleSort('hasPortalAccess')}
                          sx={{
                            '& .MuiTableSortLabel-icon': {
                              color: '#1976D2 !important',
                            },
                            '&:hover': {
                              color: '#1976D2',
                            },
                            '&.Mui-active': {
                              color: '#1976D2',
                            }
                          }}
                        >
                          Portal Access
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, py: 2, fontSize: { xs: '0.8rem', sm: '0.875rem' }, display: { xs: 'none', lg: 'table-cell' } }}>
                        <TableSortLabel
                          active={sortConfig.key === 'downloadLimit'}
                          direction={sortConfig.key === 'downloadLimit' ? sortConfig.direction : 'asc'}
                          onClick={() => handleSort('downloadLimit')}
                          sx={{
                            '& .MuiTableSortLabel-icon': {
                              color: '#1976D2 !important',
                            },
                            '&:hover': {
                              color: '#1976D2',
                            },
                            '&.Mui-active': {
                              color: '#1976D2',
                            }
                          }}
                        >
                          Download Limit
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, py: 2, fontSize: { xs: '0.8rem', sm: '0.875rem' }, display: { xs: 'none', lg: 'table-cell' } }}>
                        <TableSortLabel
                          active={sortConfig.key === 'lastLogin'}
                          direction={sortConfig.key === 'lastLogin' ? sortConfig.direction : 'asc'}
                          onClick={() => handleSort('lastLogin')}
                          sx={{
                            '& .MuiTableSortLabel-icon': {
                              color: '#1976D2 !important',
                            },
                            '&:hover': {
                              color: '#1976D2',
                            },
                            '&.Mui-active': {
                              color: '#1976D2',
                            }
                          }}
                        >
                          Last Login
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, py: 2, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>                  <TableBody>
                    {getSortedUsers(filteredUsers).map((user) => (
                      <TableRow 
                        key={user.id}
                        hover
                        sx={{ 
                          '&:hover': { bgcolor: alpha('#1976D2', 0.02) },
                          transition: 'background-color 0.2s'
                        }}
                      >
                        <TableCell sx={{ py: { xs: 1, sm: 2 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                            <Avatar 
                              sx={{ 
                                width: { xs: 28, sm: 32 }, 
                                height: { xs: 28, sm: 32 }, 
                                bgcolor: user.isAdmin ? '#424242' : '#1976D2',
                                fontSize: { xs: '0.75rem', sm: '0.875rem' }
                              }}
                            >
                              {user.name.charAt(0).toUpperCase()}
                            </Avatar>
                            <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                {user.name}
                              </Typography>                              <Typography variant="caption" sx={{ display: { xs: 'block', sm: 'none' }, color: 'text.secondary', fontSize: '0.7rem' }}>
                                {user.upn}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: { xs: 1, sm: 2 }, display: { xs: 'none', sm: 'table-cell' } }}>
                          <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                            {user.upn}
                          </Typography>
                        </TableCell><TableCell sx={{ py: { xs: 1, sm: 2 } }}>
                          <Chip 
                            label={String(user.role || 'no-data-access')} 
                            size="small"
                            sx={{
                              fontWeight: 500,
                              fontSize: { xs: '0.65rem', sm: '0.75rem' },
                              height: { xs: 20, sm: 24 },
                              bgcolor: 
                                (user.role || 'no-data-access') === 'all-data-access' ? '#E3F2FD' : 
                                (user.role || 'no-data-access') === 'except-cost-data' ? '#E3F2FD' : 
                                (user.role || 'no-data-access') === 'except-sales-data' ? '#F5F5F5' : 
                                '#F5F5F5',
                              color: 
                                (user.role || 'no-data-access') === 'all-data-access' ? '#1976D2' : 
                                (user.role || 'no-data-access') === 'except-cost-data' ? '#1976D2' : 
                                (user.role || 'no-data-access') === 'except-sales-data' ? '#424242' : 
                                '#757575',
                              border: 'none'
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: { xs: 1, sm: 2 }, display: { xs: 'none', md: 'table-cell' } }}>
                          {user.isAdmin ? 
                            <Chip 
                              icon={<SecurityIcon fontSize="small" sx={{ color: '#424242' }} />} 
                              label="Yes" 
                              size="small" 
                              sx={{ 
                                bgcolor: '#F5F5F5', 
                                color: '#424242',
                                fontWeight: 500,
                                border: 'none',
                                fontSize: '0.75rem',
                                height: 24
                              }} 
                            /> :
                            <Chip 
                              label="No" 
                              size="small" 
                              sx={{ 
                                bgcolor: '#F5F5F5',
                                color: '#757575',
                                fontWeight: 500,
                                border: 'none',
                                fontSize: '0.75rem',
                                height: 24
                              }} 
                            />
                          }
                        </TableCell>
                        <TableCell sx={{ py: { xs: 1, sm: 2 }, display: { xs: 'none', md: 'table-cell' } }}>
                          {user.hasPortalAccess ? 
                            <Chip 
                              icon={<Icon icon="mdi:chat" style={{ fontSize: '14px', color: '#1976D2' }} />} 
                              label="Enabled" 
                              size="small" 
                              sx={{ 
                                bgcolor: '#E3F2FD', 
                                color: '#1976D2',
                                fontWeight: 500,
                                border: 'none',
                                fontSize: '0.75rem',
                                height: 24
                              }} 
                            /> :
                            <Chip 
                              label="Disabled" 
                              size="small" 
                              sx={{ 
                                bgcolor: '#F5F5F5',
                                color: '#757575',
                                fontWeight: 500,
                                border: 'none',
                                fontSize: '0.75rem',
                                height: 24
                              }} 
                            />
                          }
                        </TableCell>
                        <TableCell sx={{ py: { xs: 1, sm: 2 }, display: { xs: 'none', lg: 'table-cell' } }}>
                          {(user.downloadLimit || 0) === 0 ? 
                            <Chip 
                              label="No Downloads" 
                              size="small" 
                              sx={{ 
                                bgcolor: '#F5F5F5', 
                                color: '#757575',
                                fontWeight: 500,
                                border: 'none',
                                fontSize: '0.7rem',
                                height: 24
                              }} 
                            /> : 
                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                              {(user.downloadLimit || 0).toLocaleString()} rows
                            </Typography>
                          }
                        </TableCell>
                        <TableCell sx={{ py: { xs: 1, sm: 2 }, display: { xs: 'none', lg: 'table-cell' } }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                            {formatDate(user.lastLogin)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: { xs: 1, sm: 2 } }}>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: { xs: 0.5, sm: 1 }, flexDirection: { xs: 'column', sm: 'row' } }}>
                            <Tooltip title="Edit user">
                              <IconButton 
                                size="small"
                                onClick={() => handleOpenUserDialog(user)}
                                sx={{ 
                                  color: '#1976D2',
                                  bgcolor: alpha('#1976D2', 0.1),
                                  width: { xs: 28, sm: 32 },
                                  height: { xs: 28, sm: 32 },
                                  '&:hover': {
                                    bgcolor: alpha('#1976D2', 0.2),
                                  }
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete user">
                              <IconButton 
                                size="small"
                                onClick={() => handleDeleteUser(user.id)}
                                sx={{ 
                                  color: '#757575',
                                  bgcolor: alpha('#757575', 0.1),
                                  width: { xs: 28, sm: 32 },
                                  height: { xs: 28, sm: 32 },
                                  '&:hover': {
                                    bgcolor: alpha('#757575', 0.2),
                                  }
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
            {/* Data Access Control Tab */}
          {activeTab === 1 && (
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="h6" sx={{ 
                fontWeight: 600, 
                color: '#1976D2', 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1, 
                mb: 3,
                fontSize: { xs: '1.1rem', sm: '1.25rem' }
              }}>
                <Icon icon="mdi:table-cog" width={24} height={24} />
                Configure Data Access by Category
              </Typography>
              
              <Grid container spacing={{ xs: 2, sm: 3 }}>
                <Grid item xs={12} lg={6}>
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      p: 0, 
                      height: '100%', 
                      borderRadius: '12px',
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: '#E3F2FD',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                    }}
                  >
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between', 
                      bgcolor: '#E3F2FD',
                      p: { xs: 1.5, sm: 2 },
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: { xs: 1, sm: 0 }
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ bgcolor: '#FFFFFF', width: 32, height: 32 }}>
                          <Icon icon="mdi:table" width={18} height={18} style={{ color: '#1976D2' }} />
                        </Avatar>
                        <Typography variant="h6" sx={{ 
                          fontWeight: 600, 
                          color: '#1976D2', 
                          fontSize: { xs: '1rem', sm: '1rem' }
                        }}>
                          Sales Tables
                        </Typography>
                      </Box>
                      <FormControlLabel
                        control={
                          <Switch 
                            checked={tablePermissions.sales}
                            onChange={(e) => toggleTablePermission('sales', e.target.checked)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: '#1976D2',
                                '&:hover': {
                                  backgroundColor: alpha('#1976D2', 0.1),
                                },
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: '#1976D2',
                              },
                            }}
                          />
                        }
                        label={
                          <Typography variant="body2" sx={{ 
                            fontWeight: 500, 
                            color: tablePermissions.sales ? '#1976D2' : 'text.secondary',
                            fontSize: { xs: '0.8rem', sm: '0.875rem' }
                          }}>
                            {tablePermissions.sales ? "Enabled" : "Disabled"}
                          </Typography>
                        }
                      />
                    </Box>
                    
                    <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                      <Box sx={{ maxHeight: 400, overflow: 'auto', px: { xs: 0.5, sm: 1 } }}>                        {mockTables.sales.map(table => (
                          <Card 
                            key={table.id} 
                            variant="outlined" 
                            sx={{ 
                              mb: 2, 
                              borderRadius: '10px',
                              transition: 'all 0.2s',
                              borderColor: tablePermissions.sales ? '#E3F2FD' : '#EEEEEE',
                              opacity: tablePermissions.sales ? 1 : 0.5,
                              '&:hover': tablePermissions.sales ? {
                                borderColor: '#1976D2',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                              } : {}
                            }}
                          >
                            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
                                <Typography variant="subtitle1" sx={{ 
                                  fontWeight: 600, 
                                  color: tablePermissions.sales ? '#1976D2' : 'text.disabled',
                                  fontSize: { xs: '0.9rem', sm: '1rem' }
                                }}>
                                  {table.name}
                                </Typography>
                                <Chip 
                                  size="small" 
                                  label={`ID: ${table.id}`} 
                                  sx={{ 
                                    bgcolor: '#E3F2FD', 
                                    color: '#1976D2',
                                    fontWeight: 500,
                                    height: 24,
                                    fontSize: { xs: '0.65rem', sm: '0.7rem' }
                                  }} 
                                />
                              </Box>
                              <Typography variant="body2" color={tablePermissions.sales ? "text.secondary" : "text.disabled"} sx={{ 
                                mb: 1.5,
                                fontSize: { xs: '0.75rem', sm: '0.875rem' }
                              }}>
                                {table.description}
                              </Typography>
                              <Divider sx={{ my: 1, borderColor: tablePermissions.sales ? '#E3F2FD' : '#EEEEEE' }} />
                              <Typography variant="caption" sx={{ 
                                display: 'block', 
                                color: tablePermissions.sales ? '#1976D2' : 'text.disabled', 
                                mb: 1, 
                                fontWeight: 500,
                                fontSize: { xs: '0.7rem', sm: '0.75rem' }
                              }}>
                                Available Columns ({table.columns.length})
                              </Typography>                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {table.columns.map((column, index) => (
                                  <Chip
                                    key={index}
                                    label={String(column)}
                                    size="small"
                                    variant="outlined"
                                    sx={{ 
                                      fontSize: { xs: '0.65rem', sm: '0.7rem' }, 
                                      height: { xs: 20, sm: 22 },
                                      borderColor: tablePermissions.sales ? '#E3F2FD' : '#EEEEEE',
                                      color: tablePermissions.sales ? 'text.primary' : 'text.disabled',
                                      '& .MuiChip-label': {
                                        px: { xs: 0.5, sm: 1 }
                                      }
                                    }}
                                  />
                                ))}
                              </Box>
                            </CardContent>
                          </Card>
                        ))}
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
                  <Grid item xs={12} lg={6}>
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      p: 0, 
                      height: '100%', 
                      borderRadius: '12px',
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: '#F5F5F5',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                    }}
                  >
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between', 
                      bgcolor: '#F5F5F5',
                      p: { xs: 1.5, sm: 2 },
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: { xs: 1, sm: 0 }
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ bgcolor: '#FFFFFF', width: 32, height: 32 }}>
                          <Icon icon="mdi:table" width={18} height={18} style={{ color: '#424242' }} />
                        </Avatar>
                        <Typography variant="h6" sx={{ 
                          fontWeight: 600, 
                          color: '#424242', 
                          fontSize: { xs: '1rem', sm: '1rem' }
                        }}>
                          Cost Tables
                        </Typography>
                      </Box>
                      <FormControlLabel
                        control={
                          <Switch 
                            checked={tablePermissions.cost}
                            onChange={(e) => toggleTablePermission('cost', e.target.checked)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: '#424242',
                                '&:hover': {
                                  backgroundColor: alpha('#424242', 0.1),
                                },
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: '#424242',
                              },
                            }}
                          />
                        }
                        label={
                          <Typography variant="body2" sx={{ 
                            fontWeight: 500, 
                            color: tablePermissions.cost ? '#424242' : 'text.secondary',
                            fontSize: { xs: '0.8rem', sm: '0.875rem' }
                          }}>
                            {tablePermissions.cost ? "Enabled" : "Disabled"}
                          </Typography>
                        }
                      />
                    </Box>
                    
                    <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                      <Box sx={{ maxHeight: 400, overflow: 'auto', px: { xs: 0.5, sm: 1 } }}>
                        {mockTables.cost.map(table => (
                          <Card 
                            key={table.id} 
                            variant="outlined" 
                            sx={{ 
                              mb: 2, 
                              borderRadius: '10px',
                              transition: 'all 0.2s',
                              borderColor: tablePermissions.cost ? '#F5F5F5' : '#EEEEEE',
                              opacity: tablePermissions.cost ? 1 : 0.5,
                              '&:hover': tablePermissions.cost ? {
                                borderColor: '#424242',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                              } : {}
                            }}
                          >                            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
                                <Typography variant="subtitle1" sx={{ 
                                  fontWeight: 600, 
                                  color: tablePermissions.cost ? '#424242' : 'text.disabled',
                                  fontSize: { xs: '0.9rem', sm: '1rem' }
                                }}>
                                  {table.name}
                                </Typography>
                                <Chip 
                                  size="small" 
                                  label={`ID: ${table.id}`} 
                                  sx={{ 
                                    bgcolor: '#F5F5F5', 
                                    color: '#424242',
                                    fontWeight: 500,
                                    height: 24,
                                    fontSize: { xs: '0.65rem', sm: '0.7rem' }
                                  }} 
                                />
                              </Box>
                              <Typography variant="body2" color={tablePermissions.cost ? "text.secondary" : "text.disabled"} sx={{ 
                                mb: 1.5,
                                fontSize: { xs: '0.75rem', sm: '0.875rem' }
                              }}>
                                {table.description}
                              </Typography>
                              <Divider sx={{ my: 1, borderColor: tablePermissions.cost ? '#F5F5F5' : '#EEEEEE' }} />
                              <Typography variant="caption" sx={{ 
                                display: 'block', 
                                color: tablePermissions.cost ? '#424242' : 'text.disabled', 
                                mb: 1, 
                                fontWeight: 500,
                                fontSize: { xs: '0.7rem', sm: '0.75rem' }
                              }}>
                                Available Columns ({table.columns.length})
                              </Typography>                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {table.columns.map((column, index) => (
                                  <Chip
                                    key={index}
                                    label={String(column)}
                                    size="small"
                                    variant="outlined"
                                    sx={{ 
                                      fontSize: { xs: '0.65rem', sm: '0.7rem' }, 
                                      height: { xs: 20, sm: 22 },
                                      borderColor: tablePermissions.cost ? '#F5F5F5' : '#EEEEEE',
                                      color: tablePermissions.cost ? 'text.primary' : 'text.disabled',
                                      '& .MuiChip-label': {
                                        px: { xs: 0.5, sm: 1 }
                                      }
                                    }}
                                  />
                                ))}
                              </Box>
                            </CardContent>
                          </Card>
                        ))}
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
            {/* Download Limits Tab */}
          {activeTab === 2 && (
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="h6" sx={{ 
                fontWeight: 600, 
                color: '#424242', 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1, 
                mb: 3,
                fontSize: { xs: '1.1rem', sm: '1.25rem' }
              }}>
                <Icon icon="mdi:download-box" width={24} height={24} />
                Download Limits Configuration
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ 
                mb: 3,
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}>
                Configure download limits per individual user. Set to 0 to prevent downloads completely.
              </Typography>
              
              {/* Results Summary for Download Limits */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                mb: 2,
                py: 1,
                px: 2,
                bgcolor: alpha('#1976D2', 0.04),
                borderRadius: 1,
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1, sm: 0 }
              }}>
                <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                  Managing download limits for {users.length} users
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total users with download limits: {users.filter(u => (u.downloadLimit || 0) > 0).length}
                </Typography>
              </Box>
              
              <Grid container spacing={{ xs: 2, sm: 3 }}>
                <Grid item xs={12}>
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      borderRadius: '12px',
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: '#E3F2FD',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                    }}
                  >
                    <Box 
                      sx={{ 
                        bgcolor: '#E3F2FD', 
                        p: { xs: 1.5, sm: 2 },
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <Avatar sx={{ bgcolor: '#FFFFFF', width: 32, height: 32 }}>
                        <Icon icon="mdi:account-multiple" width={18} height={18} style={{ color: '#1976D2' }} />
                      </Avatar>
                      <Typography variant="h6" sx={{ 
                        fontWeight: 600, 
                        color: '#1976D2', 
                        fontSize: { xs: '1rem', sm: '1rem' }
                      }}>
                        User Download Limits
                      </Typography>
                    </Box>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                      <Table sx={{ minWidth: { xs: 600, sm: 650 } }}>
                        <TableHead sx={{ bgcolor: alpha('#1976D2', 0.03) }}>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600, py: 2, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>User</TableCell>
                            <TableCell sx={{ fontWeight: 600, py: 2, fontSize: { xs: '0.8rem', sm: '0.875rem' }, display: { xs: 'none', sm: 'table-cell' } }}>UPN</TableCell>
                            <TableCell sx={{ fontWeight: 600, py: 2, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>Download Limit (rows)</TableCell>
                            <TableCell sx={{ fontWeight: 600, py: 2, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>Actions</TableCell>
                          </TableRow>
                        </TableHead>                        <TableBody>
                          {users.map(user => (
                            <TableRow 
                              key={user.id}
                              sx={{ 
                                '&:hover': { bgcolor: alpha('#1976D2', 0.02) },
                                transition: 'background-color 0.2s'
                              }}
                            >
                              <TableCell sx={{ py: { xs: 1, sm: 2 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                                  <Avatar 
                                    sx={{ 
                                      width: { xs: 28, sm: 32 }, 
                                      height: { xs: 28, sm: 32 }, 
                                      bgcolor: user.isAdmin ? '#424242' : '#1976D2',
                                      fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                    }}
                                  >
                                    {user.name.charAt(0).toUpperCase()}
                                  </Avatar>
                                  <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                                    <Typography variant="body2" sx={{ 
                                      fontWeight: 500,
                                      fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                    }}>
                                      {user.name}
                                    </Typography>                                    <Typography variant="caption" sx={{ 
                                      display: { xs: 'block', sm: 'none' }, 
                                      color: 'text.secondary', 
                                      fontSize: '0.7rem' 
                                    }}>
                                      {user.upn}
                                    </Typography>
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell sx={{ py: { xs: 1, sm: 2 }, display: { xs: 'none', sm: 'table-cell' } }}>
                                <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                  {user.upn}
                                </Typography></TableCell>
                              <TableCell sx={{ py: { xs: 1, sm: 2 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, flexDirection: { xs: 'column', sm: 'row' } }}>
                                  <TextField
                                    type="number"
                                    size="small"
                                    value={user.downloadLimit || 0}
                                    sx={{ 
                                      width: { xs: '80px', sm: '100px' },
                                      '& .MuiOutlinedInput-root': {
                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                          borderColor: '#1976D2',
                                        }
                                      }
                                    }}
                                  />
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                    rows
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell sx={{ py: { xs: 1, sm: 2 } }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<SaveIcon fontSize="small" />}
                                  sx={{ 
                                    textTransform: 'none',
                                    borderColor: '#1976D2',
                                    color: '#1976D2',
                                    fontSize: { xs: '0.7rem', sm: '0.875rem' },
                                    px: { xs: 1, sm: 2 },
                                    py: { xs: 0.5, sm: 1 },
                                    '&:hover': {
                                      bgcolor: alpha('#1976D2', 0.05),
                                      borderColor: '#1976D2',
                                    }
                                  }}
                                >
                                  Save
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </Paper>
      </Container>
        {/* User Edit Dialog */}      <Dialog 
        open={userDialogOpen} 
        onClose={handleCloseUserDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: '12px' },
            boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
            overflow: 'hidden',
            margin: { xs: 0, sm: 2 },
            height: { xs: '100vh', sm: 'auto' }
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: '#E3F2FD', 
          py: { xs: 1.5, sm: 2 },
          px: { xs: 2, sm: 3 },
          display: 'flex',
          alignItems: 'center',
          gap: 1.5
        }}>
          <Avatar sx={{ 
            bgcolor: selectedUser?.isAdmin ? '#424242' : '#1976D2',
            width: { xs: 32, sm: 40 },
            height: { xs: 32, sm: 40 }
          }}>
            {selectedUser?.name ? selectedUser.name.charAt(0).toUpperCase() : "U"}
          </Avatar>
          <Typography variant="h6" sx={{ 
            fontWeight: 600, 
            color: '#1976D2',
            fontSize: { xs: '1.1rem', sm: '1.25rem' }
          }}>
            {selectedUser && selectedUser.id && users.some(u => u.id === selectedUser.id)
              ? `Edit User: ${selectedUser.name}`
              : 'Add New User'
            }
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 2, sm: 3 }, height: { xs: 'calc(100vh - 140px)', sm: 'auto' }, overflowY: 'auto' }}>
          <Grid container spacing={{ xs: 2, sm: 2 }}>            <Grid item xs={12}>
              <TextField
                label="Full Name"
                fullWidth
                value={selectedUser?.name || ''}
                onChange={(e) => handleUserFieldChange('name', e.target.value)}
                margin="normal"
                variant="outlined"
                size="small"
                placeholder="Enter user's full name"
                InputLabelProps={{ 
                  sx: { 
                    fontWeight: 500,
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  } 
                }}
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    backgroundColor: 'background.paper',
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    minHeight: 'auto',
                    '& input': {
                      padding: { xs: '8px 12px', sm: '10px 14px' },
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(144, 202, 249, 0.5)' : alpha('#1976D2', 0.5),
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: (theme) => theme.palette.mode === 'dark' ? '#90caf9' : '#1976D2',
                    }
                  }
                }}
              />
            </Grid><Grid item xs={12}>
              <TextField
                label="UPN (User Principal Name)"
                fullWidth
                value={selectedUser?.upn || ''}
                onChange={(e) => handleUserFieldChange('upn', e.target.value)}
                margin="normal"
                variant="outlined"
                size="small"
                placeholder="user@ethosenergy.com"
                InputLabelProps={{ 
                  sx: { 
                    fontWeight: 500,
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  } 
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon 
                        icon="mdi:account-outline" 
                        style={{ 
                          color: 'text.secondary', 
                          width: 18, 
                          height: 18 
                        }} 
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    backgroundColor: 'background.paper',
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    minHeight: 'auto',
                    '& input': {
                      padding: { xs: '8px 4px', sm: '10px 6px' },
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(144, 202, 249, 0.5)' : alpha('#1976D2', 0.5),
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: (theme) => theme.palette.mode === 'dark' ? '#90caf9' : '#1976D2',
                    }
                  },
                  '& .MuiInputAdornment-root': {
                    color: 'text.secondary',
                  }
                }}
              />
            </Grid>            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal" variant="outlined" size="small" sx={{ 
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: 'background.paper',
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  minHeight: 'auto',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(144, 202, 249, 0.5)' : alpha('#1976D2', 0.5),
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.mode === 'dark' ? '#90caf9' : '#1976D2',
                  }
                }
              }}>
                <InputLabel sx={{ 
                  fontWeight: 500,
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}>Role</InputLabel><Select
                  value={selectedUser?.role || 'no-data-access'}
                  onChange={(e) => handleUserFieldChange('role', e.target.value)}
                  label="Role"
                ><MenuItem value="no-data-access">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip 
                        size="small" 
                        label="no-data-access" 
                        sx={{ 
                          bgcolor: '#F5F5F5', 
                          color: '#757575', 
                          fontWeight: 500,
                          height: 24,
                          minWidth: 80,
                          fontSize: { xs: '0.7rem', sm: '0.75rem' }
                        }} 
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                        No data access
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="except-cost-data">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip 
                        size="small" 
                        label="except-cost-data" 
                        sx={{ 
                          bgcolor: '#E3F2FD', 
                          color: '#1976D2', 
                          fontWeight: 500,
                          height: 24,
                          minWidth: 80,
                          fontSize: { xs: '0.7rem', sm: '0.75rem' }
                        }} 
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                        Except Cost data
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="except-sales-data">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip 
                        size="small" 
                        label="except-sales-data" 
                        sx={{ 
                          bgcolor: '#F5F5F5', 
                          color: '#424242', 
                          fontWeight: 500,
                          height: 24,
                          minWidth: 80,
                          fontSize: { xs: '0.7rem', sm: '0.75rem' }
                        }} 
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                         Except Sales data
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="all-data-access">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip 
                        size="small" 
                        label="all-data-access" 
                        sx={{ 
                          bgcolor: '#E3F2FD', 
                          color: '#1976D2', 
                          fontWeight: 500,
                          height: 24,
                          minWidth: 80,
                          fontSize: { xs: '0.7rem', sm: '0.75rem' }
                        }} 
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                        All data access
                      </Typography>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>            <Grid item xs={12} sm={6}>
              <TextField
                label="Download Limit"
                fullWidth
                type="number"
                value={selectedUser?.downloadLimit || 0}
                onChange={(e) => handleUserFieldChange('downloadLimit', parseInt(e.target.value, 10))}
                margin="normal"
                variant="outlined"
                size="small"
                inputProps={{ 
                  min: 0, 
                  max: 10000,
                  style: { 
                    textAlign: 'center',
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
                InputLabelProps={{ 
                  sx: { 
                    fontWeight: 500,
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  } 
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon 
                        icon="mdi:download-outline" 
                        style={{ 
                          color: 'text.secondary', 
                          width: 18, 
                          height: 18 
                        }} 
                      />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        rows
                      </Typography>
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    backgroundColor: 'background.paper',
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    minHeight: 'auto',
                    '& input': {
                      padding: { xs: '8px 4px', sm: '10px 6px' },
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(156, 39, 176, 0.5)' : alpha('#9c27b0', 0.5),
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: (theme) => theme.palette.mode === 'dark' ? '#ba68c8' : '#9c27b0',
                    }
                  },
                  '& .MuiInputAdornment-root': {
                    color: 'text.secondary',
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Paper 
                variant="outlined" 
                sx={{ 
                  p: { xs: 1.5, sm: 2 }, 
                  mt: 1, 
                  borderRadius: '8px',
                  borderColor: selectedUser?.isAdmin ? alpha('#424242', 0.5) : alpha('#1976D2', 0.2),
                  bgcolor: selectedUser?.isAdmin ? alpha('#424242', 0.05) : alpha('#1976D2', 0.02)
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={selectedUser?.isAdmin || false}
                      onChange={(e) => handleUserFieldChange('isAdmin', e.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#424242',
                          '&:hover': {
                            backgroundColor: alpha('#424242', 0.1),
                          },
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#424242',
                        },
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body1" sx={{ 
                        fontWeight: 600, 
                        color: selectedUser?.isAdmin ? '#424242' : '#1976D2',
                        fontSize: { xs: '0.9rem', sm: '1rem' }
                      }}>
                        Admin Privileges
                      </Typography>                      <Typography variant="caption" component="div" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                        Allows user to configure portal settings but does not grant additional data access.
                      </Typography>
                    </Box>
                  }
                />
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Paper 
                variant="outlined" 
                sx={{ 
                  p: { xs: 1.5, sm: 2 }, 
                  mt: 1, 
                  borderRadius: '8px',
                  borderColor: selectedUser?.hasPortalAccess ? alpha('#1976D2', 0.5) : alpha('#757575', 0.2),
                  bgcolor: selectedUser?.hasPortalAccess ? alpha('#1976D2', 0.05) : alpha('#757575', 0.02)
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={selectedUser?.hasPortalAccess || false}
                      onChange={(e) => handleUserFieldChange('hasPortalAccess', e.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#1976D2',
                          '&:hover': {
                            backgroundColor: alpha('#1976D2', 0.1),
                          },
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#1976D2',
                        },
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body1" sx={{ 
                        fontWeight: 600, 
                        color: selectedUser?.hasPortalAccess ? '#1976D2' : '#757575',
                        fontSize: { xs: '0.9rem', sm: '1rem' }
                      }}>
                        Portal Access
                      </Typography>
                      <Typography variant="caption" component="div" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                        Allows user to access the conversation chat interface and interact with the AI assistant.
                      </Typography>
                    </Box>
                  }
                />
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ 
          p: { xs: 2, sm: 2.5 }, 
          bgcolor: alpha('#1976D2', 0.02),
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 0 }
        }}>          <Button 
            onClick={handleCloseUserDialog}
            variant="outlined"
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              borderColor: alpha('#1976D2', 0.5),
              color: '#1976D2',
              px: 3,
              order: { xs: 2, sm: 1 },
              width: { xs: '100%', sm: 'auto' },
              '&:hover': {
                borderColor: '#1976D2',
                bgcolor: alpha('#1976D2', 0.05)
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveUser}
            variant="contained"
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              bgcolor: '#1976D2',
              px: 3,
              order: { xs: 1, sm: 2 },
              width: { xs: '100%', sm: 'auto' },
              '&:hover': {
                bgcolor: '#1565C0',
              }
            }}
            startIcon={<SaveIcon />}
          >
            Save User
          </Button>
        </DialogActions>
      </Dialog>
        {/* Snackbar for notifications */}      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ 
          vertical: 'bottom', 
          horizontal: isMobile ? 'center' : 'right'
        }}
        sx={{
          '& .MuiSnackbar-root': {
            bottom: { xs: 16, sm: 24 },
            left: { xs: 16, sm: 'auto' },
            right: { xs: 16, sm: 24 }
          }
        }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ 
            width: '100%',
            borderRadius: '8px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
            alignItems: 'center',
            fontSize: { xs: '0.8rem', sm: '0.875rem' },
            '& .MuiAlert-icon': {
              fontSize: '1.2rem',
              opacity: 0.9
            },
            ...(snackbar.severity === 'success' && {
              bgcolor: '#1976D2',
            }),
            ...(snackbar.severity === 'error' && {
              bgcolor: '#757575',
            }),
            ...(snackbar.severity === 'warning' && {
              bgcolor: '#424242',
            }),
            ...(snackbar.severity === 'info' && {
              bgcolor: '#1976D2',
            })
          }}        >          {snackbar.message}
        </Alert>
      </Snackbar>
        </>
      )}
    </Box>
  );
}